import { Box, Tooltip, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { useTranslation } from "react-i18next";
import { BookingStatus } from "src/business/booking";
import {
  colorSlotConfirmed,
  colorSlotConfirmedHover,
  colorSlotConfirmedText,
  colorSlotPending,
  colorSlotPendingHover,
  colorSlotPendingText,
  radiusChip,
} from "src/design/tokens";
import {
  BookingSegment,
  DayView,
  FULL_DAY_VIEW,
  slotLabelForRow,
  viewLengthPercent,
  viewTopPercent,
} from "../dayGridHelpers";

interface BlockStyle {
  bg: string;
  hover: string;
  color: string;
}

// A confirmed booking takes the solid emerald, anything still awaiting
// confirmation the light one — same scale the rest of the app uses for slots.
const BLOCK_STYLES: Record<"confirmed" | "pending", BlockStyle> = {
  confirmed: {
    bg: colorSlotConfirmed,
    hover: colorSlotConfirmedHover,
    color: colorSlotConfirmedText,
  },
  pending: {
    bg: colorSlotPending,
    hover: colorSlotPendingHover,
    color: colorSlotPendingText,
  },
};

interface BookingBlockProps {
  segment: BookingSegment;
  /** Rows the grid is showing; the block is positioned within it. */
  view?: DayView;
  isDragging: boolean;
  onPointerDown: (event: React.PointerEvent) => void;
  onClick: () => void;
  sx?: SxProps;
}

/**
 * One booking drawn over its table's column, spanning a row per half hour of the
 * restaurant's booking duration. The edge that continues onto the neighbouring
 * day is squared off and arrowed so a spill-over reads as one booking cut in two
 * rather than two bookings.
 */
export const BookingBlock = ({
  segment,
  view = FULL_DAY_VIEW,
  isDragging,
  onPointerDown,
  onClick,
  sx,
}: BookingBlockProps) => {
  const { t } = useTranslation();
  const { booking, startRow, endRow, startsBeforeToday, endsAfterToday } = segment;

  const style =
    booking.bookingStatus === BookingStatus.CONFIRMED
      ? BLOCK_STYLES.confirmed
      : BLOCK_STYLES.pending;

  const rows = endRow - startRow;
  const startLabel = slotLabelForRow(segment.absStartRow);
  const endLabel = slotLabelForRow(segment.absStartRow + segment.totalRows);

  const tooltip = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
        {booking.userName}
      </Typography>
      <Typography sx={{ fontSize: 12.5 }}>
        {startLabel} – {endLabel}
      </Typography>
      <Typography sx={{ fontSize: 12.5 }}>
        {t("tableSchedule.inspector.seats")}: {booking.numberOfSeats}
      </Typography>
      <Typography sx={{ fontSize: 12.5 }}>
        {t(`tableSchedule.status.${booking.bookingStatus}`)}
      </Typography>
      {booking.dealContent && (
        <Typography sx={{ fontSize: 12.5 }}>
          {t("tableSchedule.inspector.deal")}: {booking.dealContent}
        </Typography>
      )}
      {booking.eventContent && (
        <Typography sx={{ fontSize: 12.5 }}>
          {t("tableSchedule.inspector.event")}: {booking.eventContent}
        </Typography>
      )}
      {startsBeforeToday && (
        <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>
          {t("tableSchedule.block.startedPrevDay")}
        </Typography>
      )}
      {endsAfterToday && (
        <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>
          {t("tableSchedule.block.continuesNextDay")}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="right" disableInteractive>
      <Box
        onPointerDown={onPointerDown}
        onClick={onClick}
        sx={{
          position: "absolute",
          top: viewTopPercent(startRow, view),
          height: viewLengthPercent(rows, view),
          left: `${(segment.lane / segment.laneCount) * 100}%`,
          width: `${(1 / segment.laneCount) * 100}%`,
          px: 0.5,
          boxSizing: "border-box",
          backgroundColor: style.bg,
          color: style.color,
          // Square off whichever edge runs into the neighbouring day.
          borderTopLeftRadius: startsBeforeToday ? 0 : radiusChip,
          borderTopRightRadius: startsBeforeToday ? 0 : radiusChip,
          borderBottomLeftRadius: endsAfterToday ? 0 : radiusChip,
          borderBottomRightRadius: endsAfterToday ? 0 : radiusChip,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.4,
          overflow: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          opacity: isDragging ? 0.4 : 1,
          zIndex: isDragging ? 1 : 2,
          transition: "background-color 120ms ease, opacity 120ms ease",
          ":hover": { backgroundColor: style.hover },
          ...sx,
        }}
      >
        {startsBeforeToday && <NorthIcon sx={{ fontSize: 10, flexShrink: 0 }} />}
        <EventSeatIcon sx={{ fontSize: 11, flexShrink: 0 }} />
        <Typography
          component="span"
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {booking.numberOfSeats}
          {rows > 1 ? ` · ${booking.userName}` : ""}
        </Typography>
        {endsAfterToday && <SouthIcon sx={{ fontSize: 10, flexShrink: 0 }} />}
      </Box>
    </Tooltip>
  );
};
